/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: EmailTestModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the emailSettings module.
----------------------------------------------------------------*/

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { X, Send, Loader2, CheckCircle, XCircle } from 'lucide-react';
import type { EmailConfig } from './types';

const testEmailSchema = z.object({
  testEmail: z.string().email('Invalid email format')
});

type TestEmailFormData = z.infer<typeof testEmailSchema>;

interface EmailTestModalProps {
  config: EmailConfig;
  onClose: () => void;
  onSuccess: () => void;
}

export function EmailTestModal({ config, onClose, onSuccess }: EmailTestModalProps) {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message?: string;
    error?: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<TestEmailFormData>({
    resolver: zodResolver(testEmailSchema),
    defaultValues: {
      testEmail: ''
    },
    mode: 'onChange'
  });

  // Set default email when component mounts
  useEffect(() => {
    setValue('testEmail', config.fromEmail ?? '');
  }, [config.fromEmail, setValue]);

  const onSubmit = async (data: TestEmailFormData) => {
    try {
      setLoading(true);
      setTestResult(null);

      const response = await fetch(buildApiUrl('/api/email-settings/test'), {
        method: 'POST',
        headers: {
          ...getApiHeaders(user?.tenant?.slug || 'main', user?.id),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          configId: config.id,
          testEmail: data.testEmail
        })
      });

      const result = await response.json();
      
      setTestResult({
        success: result.success,
        message: result.message,
        error: result.error
      });

      if (result.success) {
        showSuccess(t('emailSettings.test.success'));
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        showError(result.error || t('emailSettings.test.error'));
      }
    } catch (error) {
            setTestResult({
        success: false,
        error: t('emailSettings.test.error')
      });
      showError(t('emailSettings.test.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">
              {t('emailSettings.test.title')}
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Configuration Info */}
          <div className="mb-6 p-4 bg-muted rounded-lg">
            <h3 className="text-sm font-medium text-foreground mb-2">
              {t('emailSettings.test.configurationDetails')}
            </h3>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p><strong>Host:</strong> {config.host}</p>
              <p><strong>Port:</strong> {config.port}</p>
              <p><strong>Security:</strong> {config.secure ? 'SSL' : config.tls ? 'TLS' : 'None'}</p>
              <p><strong>Auth:</strong> {t(`emailSettings.authTypes.${config.authType}`)}</p>
              <p><strong>From:</strong> {config.fromName} &lt;{config.fromEmail}&gt;</p>
            </div>
          </div>

          {/* Test Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('emailSettings.test.testEmail')} *
              </label>
              <Input
                {...register('testEmail')}
                type="email"
                placeholder={t('emailSettings.test.testEmailPlaceholder')}
                className={errors.testEmail ? 'border-red-500' : ''}
                disabled={loading}
              />
              {errors.testEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.testEmail.message}</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                {t('emailSettings.test.description')}
              </p>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`p-4 rounded-lg ${
                testResult.success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center space-x-2">
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${
                      testResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {testResult.success ? t('emailSettings.test.testSuccessful') : t('emailSettings.test.testFailed')}
                    </p>
                    <p className={`text-xs ${
                      testResult.success ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {testResult.message || testResult.error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                <span>
                  {loading ? t('emailSettings.test.sending') : t('emailSettings.test.sendTest')}
                </span>
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
